import type { ExperimentClient, Variants } from '@amplitude/experiment-js-client';

import { createContext, ReactNode, useMemo, useState } from 'react';

import {
    type FeatureFlagCookies,
    getCookieVariantOverrides,
} from '@/utils/amplitude/experiments/utils/variantsToFeatureFlags';

export type AmplitudeExperimentsContextType = {
    client?: ExperimentClient | undefined;
    deviceId: string | null;
    /**
     * Dev-tools (Cmd+/ overlay) cookies, already emptied on prod by getFeatureFlagCookies.
     * AppHammerUIProvider layers them over the HammerUI feature flags. The provider drops the
     * cookies for keys pinned by forceExperiment, which names an exact variant and so wins.
     */
    featureFlagCookies?: FeatureFlagCookies;
    /**
     * Flag keys pinned via the non-production forceExperiment override or a dev-tools cookie.
     * Exposure tracking must NOT fire for these keys (forced devices would pollute experiment data).
     */
    overriddenFlagKeys?: string[];
    setVariants?: (variants: Variants) => void;
    userId: string | null;
    variants: Variants;
};

export type AmplitudeExperimentsContextProps = Omit<AmplitudeExperimentsContextType, 'overriddenFlagKeys'> & {
    children: ReactNode;
    /**
     * Non-production QA override layer merged over the fetched variants at render time —
     * not into the `variants` state — so it survives `setVariants` replacing the whole map
     * (AmplitudeProvider does that on every identity change).
     */
    variantOverrides?: Variants;
};

export const AmplitudeExperimentsContext = createContext<AmplitudeExperimentsContextType>({
    client: null,
    deviceId: null,
    userId: null,
    variants: {},
});

const NO_COOKIES: FeatureFlagCookies = {};

export const AmplitudeExperimentsProvider = ({
    children,
    client,
    deviceId,
    featureFlagCookies = NO_COOKIES,
    userId,
    variantOverrides,
    variants: variantsExternal,
}: AmplitudeExperimentsContextProps) => {
    const [variants, setVariants] = useState<Variants>(variantsExternal);
    // Memoized so flag consumers only re-render when an input changes, not whenever the provider does.
    const value = useMemo(() => {
        const forcedFlagKeys = Object.keys(variantOverrides ?? {});
        // forceExperiment names an exact variant, so it wins over a cookie for the same key
        const cookies =
            forcedFlagKeys.length > 0
                ? Object.fromEntries(
                      Object.entries(featureFlagCookies).filter(([flagKey]) => !forcedFlagKeys.includes(flagKey))
                  )
                : featureFlagCookies;
        const cookieVariantOverrides = getCookieVariantOverrides(variants, cookies);

        return {
            client,
            deviceId,
            featureFlagCookies: cookies,
            overriddenFlagKeys: [...forcedFlagKeys, ...Object.keys(cookieVariantOverrides)],
            setVariants,
            userId,
            variants: { ...variants, ...cookieVariantOverrides, ...variantOverrides },
        };
    }, [client, deviceId, featureFlagCookies, userId, variantOverrides, variants]);

    return <AmplitudeExperimentsContext.Provider value={value}>{children}</AmplitudeExperimentsContext.Provider>;
};
